import { BigNumber } from "bignumber.js";
import { clsx, type ClassValue } from "clsx";
import { add } from "date-fns";
import { RouterSupportedFiat, RouterTransferMethods } from "interfaces/enums";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function pluralize(word: string, count?: number): string {
  return `${count} ${count === 1 ? word : `${word}s`}`;
}

export const isReap = (transfer_method?: RouterTransferMethods) => {
  return [
    RouterTransferMethods.SWIFT,
    RouterTransferMethods.HK_CHATS,
    RouterTransferMethods.HK_FPS,
    RouterTransferMethods.SEPA,
  ].includes(transfer_method!);
};

export const getInitials = (name?: string, name2?: string) => {
  return (
    name
      ?.split(" ")
      .map((word) => word[0])
      .join("") + (name2 ? name2[0] : "")
  );
};

export const addHoursRelativeToServer = (date: string, hours?: number) => {
  // The server is currently at US/Mexico time, and we need to add 6 hours to the date to get the correct time.
  return add(new Date(Number(date) * 1000), { hours: hours || 6 });
};

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// rate_feeless (the client rate with transfer fees factored out) is null
// on orders created before the backend computed it; missing/null/0 all
// mean "not available".
export const hasFeelessRate = (
  rate?: string | number | null,
): rate is string | number => {
  const value = Number(rate);
  return Number.isFinite(value) && value > 0;
};

// A transfer-fee amount is only worth a row in the UI when it's positive;
// "0" (no-fee corridors) and missing (pre-deploy records) both mean the
// fee display should be omitted entirely.
export const hasTransferFee = (qty?: string | number | null) => {
  const value = Number(qty);
  return Number.isFinite(value) && value > 0;
};

// A transfer fee is baked into rate_client only when rate_feeless is
// present and differs from it. Equal rates (deferred fees, zero-fee
// corridors, MEX_DIVISA/manual orders) and missing rate_feeless
// (historical orders) both mean the composite rate is the only rate to
// show. Compare parsed decimals, never the raw strings.
export const hasFeeInRate = ({
  rate_client,
  rate_feeless,
}: {
  rate_client?: string | number | null;
  rate_feeless?: string | number | null;
}) => {
  if (!hasFeelessRate(rate_feeless)) return false;
  const client = BigNumber(rate_client ?? NaN);
  return !client.isNaN() && !BigNumber(rate_feeless).eq(client);
};

// In México the people is used to see the exchange in terms of MXN,
// so we need to invert the exchange rate when the source currency is MXN
// and the destination currency is not MXN. but only for currencies that have a lower value than MXN
export const shouldInvertMXNCurrency = ({
  ccy_source,
  ccy_dest,
}: {
  ccy_source?: string;
  ccy_dest?: string;
}) => {
  return (
    ccy_source === "MXN" &&
    !!ccy_dest &&
    [
      RouterSupportedFiat.USD,
      RouterSupportedFiat.HKD,
      RouterSupportedFiat.CNY,
      RouterSupportedFiat.SGD,
      RouterSupportedFiat.AUD,
      RouterSupportedFiat.NZD,
      RouterSupportedFiat.CHF,
      RouterSupportedFiat.EUR,
      RouterSupportedFiat.CAD,
      RouterSupportedFiat.DKK,
      RouterSupportedFiat.GBP,
      RouterSupportedFiat.NOK,
      RouterSupportedFiat.SEK,
    ].includes(ccy_dest as RouterSupportedFiat)
  );
};

export const invertMXNCurrency = ({
  qty,
  ccy_source,
  ccy_dest,
  precision = 4,
}: {
  qty?: string | number;
  ccy_source?: string;
  ccy_dest?: string;
  precision?: number;
}) => {
  if (!qty) return qty;
  if (shouldInvertMXNCurrency({ ccy_source, ccy_dest }))
    return BigNumber(1)
      .dividedBy(qty)
      .toFixed(precision, BigNumber.ROUND_HALF_UP);
  else return qty;
};
